import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { formatFechaHora, formatNota, isNotaDesaprobada, parseNotaInput } from '../../utils/evaluaciones';
import './Evaluaciones.css';

// Planilla de notas de una instancia: todos los alumnos del curso, una nota y un comentario por alumno.
// Se maneja con teclado (Enter/↓ siguiente, ↑ anterior, A = ausente, Esc deshace) y cada fila se guarda
// sola al salir del campo. Cada fila tiene su cola: un guardado a la vez y siempre el último valor.

const toSaved = (alumno) => ({
  nota: alumno.nota,
  ausente: Boolean(alumno.ausente),
  comentario: alumno.comentario || ''
});

const buildRow = (alumno) => ({
  idAlumno: alumno.idAlumno,
  nombre: alumno.nombre,
  email: alumno.email,
  saved: toSaved(alumno),
  notaText: alumno.ausente ? '' : formatNota(alumno.nota),
  ausente: Boolean(alumno.ausente),
  comentario: alumno.comentario || '',
  status: 'idle',
  error: ''
});

const buildPayload = (row) => {
  const parsed = parseNotaInput(row.notaText);
  return {
    error: row.ausente ? '' : parsed.error,
    payload: {
      nota: row.ausente ? null : parsed.value,
      ausente: row.ausente,
      comentario: row.comentario.trim()
    }
  };
};

const isSamePayload = (payload, saved) => (
  payload.ausente === saved.ausente &&
  (payload.nota === null ? saved.nota === null : saved.nota !== null && Number(payload.nota) === Number(saved.nota)) &&
  payload.comentario === (saved.comentario || '')
);

const SAVED_FEEDBACK_MS = 2000;

const EvaluacionNotasGrid = forwardRef(({ api, evaluacionId, onBack, onSaved }, ref) => {
  const [evaluacion, setEvaluacion] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const rowsRef = useRef([]);
  const inFlightRef = useRef(new Map());
  const pendingRef = useRef(new Map());
  const timersRef = useRef(new Map());
  const inputRefs = useRef({});

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => () => {
    timersRef.current.forEach((timer) => clearTimeout(timer));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const planilla = await api.getPlanilla(evaluacionId);
      setEvaluacion(planilla.evaluacion);
      const nextRows = planilla.alumnos.map(buildRow);
      rowsRef.current = nextRows;
      setRows(nextRows);
      setLoadError('');
    } catch (err) {
      setLoadError(err?.message || 'No pudimos cargar la planilla.');
    } finally {
      setLoading(false);
    }
  }, [api, evaluacionId]);

  useEffect(() => {
    load();
  }, [load]);

  const patchRow = useCallback((idAlumno, patch) => {
    setRows((prev) => {
      const next = prev.map((row) => (row.idAlumno === idAlumno ? { ...row, ...(typeof patch === 'function' ? patch(row) : patch) } : row));
      rowsRef.current = next;
      return next;
    });
  }, []);

  const runSave = useCallback((idAlumno, payload) => {
    clearTimeout(timersRef.current.get(idAlumno));
    patchRow(idAlumno, { status: 'saving', error: '' });

    const request = api.guardarNota(evaluacionId, idAlumno, payload)
      .then((saved) => {
        inFlightRef.current.delete(idAlumno);
        const savedValues = toSaved(saved);
        const next = pendingRef.current.get(idAlumno);
        pendingRef.current.delete(idAlumno);

        if (next && !isSamePayload(next, savedValues)) {
          patchRow(idAlumno, { saved: savedValues });
          runSave(idAlumno, next);
          return;
        }

        patchRow(idAlumno, (row) => {
          const draft = buildPayload(row);
          return {
            saved: savedValues,
            status: !draft.error && isSamePayload(draft.payload, savedValues) ? 'saved' : 'dirty',
            error: ''
          };
        });
        timersRef.current.set(idAlumno, setTimeout(() => {
          patchRow(idAlumno, (row) => (row.status === 'saved' ? { status: 'idle' } : {}));
        }, SAVED_FEEDBACK_MS));
        onSaved?.();
      })
      .catch((err) => {
        inFlightRef.current.delete(idAlumno);
        pendingRef.current.delete(idAlumno);
        patchRow(idAlumno, { status: 'error', error: err?.message || 'No se pudo guardar.' });
      });

    inFlightRef.current.set(idAlumno, request);
  }, [api, evaluacionId, onSaved, patchRow]);

  // Guarda la fila si cambió respecto de lo último guardado. "overrides" permite guardar un cambio
  // que todavía no llegó al estado (por ejemplo, el toggle de ausente).
  const commitRow = useCallback((idAlumno, overrides = null, force = false) => {
    const current = rowsRef.current.find((row) => row.idAlumno === idAlumno);
    if (!current) return;
    const row = overrides ? { ...current, ...overrides } : current;
    const draft = buildPayload(row);

    if (draft.error) {
      patchRow(idAlumno, { status: 'invalid', error: draft.error });
      return;
    }

    if (!force && !inFlightRef.current.has(idAlumno) && isSamePayload(draft.payload, row.saved)) {
      if (row.status === 'dirty' || row.status === 'invalid') {
        patchRow(idAlumno, { status: 'idle', error: '' });
      }
      return;
    }

    if (inFlightRef.current.has(idAlumno)) {
      pendingRef.current.set(idAlumno, draft.payload);
      return;
    }

    runSave(idAlumno, draft.payload);
  }, [patchRow, runSave]);

  const handleChange = (idAlumno, field, value) => {
    patchRow(idAlumno, (row) => ({
      [field]: value,
      status: row.status === 'saving' ? 'saving' : 'dirty',
      error: ''
    }));
  };

  const toggleAusente = (idAlumno) => {
    const row = rowsRef.current.find((item) => item.idAlumno === idAlumno);
    if (!row) return;
    const patch = { ausente: !row.ausente, notaText: !row.ausente ? '' : row.notaText };
    patchRow(idAlumno, patch);
    commitRow(idAlumno, patch);
  };

  const revertRow = (idAlumno) => {
    patchRow(idAlumno, (row) => ({
      notaText: row.saved.ausente ? '' : formatNota(row.saved.nota),
      ausente: row.saved.ausente,
      comentario: row.saved.comentario,
      status: 'idle',
      error: ''
    }));
  };

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) => `${row.nombre} ${row.email}`.toLowerCase().includes(term));
  }, [rows, search]);

  const focusCell = (index, field) => {
    const target = visibleRows[index];
    if (!target) return;
    const input = inputRefs.current[`${target.idAlumno}-${field}`];
    if (input) {
      input.focus();
      if (typeof input.select === 'function') input.select();
    }
  };

  const handleNotaKeyDown = (event, index, idAlumno) => {
    if (event.key === 'Enter' || event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const up = event.key === 'ArrowUp' || (event.key === 'Enter' && event.shiftKey);
      focusCell(up ? index - 1 : index + 1, 'nota');
      return;
    }
    const row = rowsRef.current.find((item) => item.idAlumno === idAlumno);
    if (event.key === 'a' || event.key === 'A' || (row?.ausente && (event.key === 'Backspace' || event.key === 'Delete'))) {
      event.preventDefault();
      toggleAusente(idAlumno);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      revertRow(idAlumno);
    }
  };

  const handleComentarioKeyDown = (event, index, idAlumno) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      focusCell(event.shiftKey ? index - 1 : index + 1, 'nota');
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      revertRow(idAlumno);
    }
  };

  const stats = useMemo(() => ({
    total: rows.length,
    calificados: rows.filter((row) => row.saved.nota !== null && row.saved.nota !== undefined).length,
    ausentes: rows.filter((row) => row.saved.ausente).length,
    sinGuardar: rows.filter((row) => row.status === 'dirty' || row.status === 'invalid').length,
    guardando: rows.filter((row) => row.status === 'saving').length,
    conError: rows.filter((row) => row.status === 'error').length
  }), [rows]);

  // Aviso del navegador si se intenta salir con notas sin guardar.
  useEffect(() => {
    if (stats.sinGuardar + stats.guardando + stats.conError === 0) return undefined;
    const handler = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [stats.conError, stats.guardando, stats.sinGuardar]);

  const retryErrors = () => {
    rowsRef.current
      .filter((row) => row.status === 'error')
      .forEach((row) => commitRow(row.idAlumno, null, true));
  };

  // Lo usa el modal antes de cerrar: guarda lo que quedó escrito y espera a que terminen los guardados.
  useImperativeHandle(ref, () => ({
    async flush() {
      rowsRef.current
        .filter((row) => row.status === 'dirty')
        .forEach((row) => commitRow(row.idAlumno));
      for (let i = 0; i < 5 && inFlightRef.current.size > 0; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await Promise.allSettled([...inFlightRef.current.values()]);
      }
      const pendientes = rowsRef.current.filter((row) => ['dirty', 'invalid', 'error', 'saving'].includes(row.status));
      return { pendientes: pendientes.length };
    }
  }), [commitRow]);

  if (loading) {
    return <div className="ev-loading"><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Cargando planilla...</div>;
  }

  if (loadError) {
    return (
      <div className="ev-empty">
        <p className="ev-error-text">{loadError}</p>
        <button type="button" className="ev-btn ev-btn-secondary" onClick={load}>Reintentar</button>
      </div>
    );
  }

  return (
    <div className="ev-grid">
      <div className="ev-grid-header">
        <button type="button" className="ev-btn ev-btn-secondary ev-btn-sm" onClick={onBack}>
          <i className="fas fa-arrow-left" aria-hidden="true" />
          Evaluaciones
        </button>
        <div className="ev-grid-title">
          <strong>{evaluacion?.nombre}</strong>
          <span>{formatFechaHora(evaluacion?.fechaHora)}{evaluacion?.nombreMateria ? ` · ${evaluacion.nombreMateria}` : ''}</span>
        </div>
        <div className="ev-grid-stats">
          <span><b>{stats.calificados}</b>/{stats.total} calificados</span>
          {stats.ausentes > 0 && <span>{stats.ausentes} {stats.ausentes === 1 ? 'ausente' : 'ausentes'}</span>}
          {stats.guardando > 0 && <span className="saving"><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Guardando</span>}
          {stats.sinGuardar > 0 && <span className="dirty">{stats.sinGuardar} sin guardar</span>}
          {stats.conError > 0 && <span className="error">{stats.conError} con error</span>}
        </div>
      </div>

      <div className="ev-toolbar">
        <div className="ev-search">
          <i className="fas fa-search" aria-hidden="true" />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar alumno"
            aria-label="Buscar alumno"
          />
        </div>
        <span className="ev-keys-help">
          <kbd>Enter</kbd>/<kbd>↓</kbd> siguiente · <kbd>↑</kbd> anterior · <kbd>A</kbd> ausente · <kbd>Esc</kbd> deshacer
        </span>
        <div className="ev-toolbar-actions">
          {stats.conError > 0 && (
            <button type="button" className="ev-btn ev-btn-danger-soft ev-btn-sm" onClick={retryErrors}>
              <i className="fas fa-redo" aria-hidden="true" />
              Reintentar
            </button>
          )}
          <button
            type="button"
            className="ev-btn ev-btn-secondary ev-btn-sm"
            onClick={load}
            disabled={stats.guardando > 0 || stats.sinGuardar > 0}
            title={stats.sinGuardar > 0 ? 'Guardá o deshacé los cambios antes de refrescar' : 'Volver a cargar las notas'}
          >
            <i className="fas fa-sync-alt" aria-hidden="true" />
            Refrescar
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="ev-empty">
          <i className="fas fa-user-graduate" aria-hidden="true" />
          <p>El curso no tiene alumnos activos.</p>
        </div>
      ) : (
        <div className="ev-table-wrap ev-grid-table-wrap">
          <table className="ev-table ev-grid-table">
            <thead>
              <tr>
                <th className="ev-col-num">#</th>
                <th>Alumno</th>
                <th className="ev-col-nota">Nota</th>
                <th className="ev-col-ausente">Ausente</th>
                <th>Comentario <small>(por qué de la nota)</small></th>
                <th className="ev-col-status" aria-label="Estado" />
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, index) => {
                const parsed = parseNotaInput(row.notaText);
                const notaBaja = !row.ausente && !parsed.error && isNotaDesaprobada(parsed.value);
                return (
                  <tr key={row.idAlumno} className={`ev-row-${row.status}${row.ausente ? ' is-ausente' : ''}`}>
                    <td className="ev-col-num">{index + 1}</td>
                    <td>
                      <strong>{row.nombre}</strong>
                      {row.email && <small className="ev-cell-sub">{row.email}</small>}
                    </td>
                    <td className="ev-col-nota">
                      <input
                        ref={(element) => { inputRefs.current[`${row.idAlumno}-nota`] = element; }}
                        type="text"
                        inputMode="decimal"
                        className={`ev-nota-input${row.status === 'invalid' ? ' invalid' : ''}${notaBaja ? ' baja' : ''}`}
                        value={row.ausente ? 'A' : row.notaText}
                        onChange={(event) => !row.ausente && handleChange(row.idAlumno, 'notaText', event.target.value)}
                        onKeyDown={(event) => handleNotaKeyDown(event, index, row.idAlumno)}
                        onBlur={() => commitRow(row.idAlumno)}
                        readOnly={row.ausente}
                        placeholder="—"
                        aria-label={`Nota de ${row.nombre}`}
                        autoComplete="off"
                        maxLength={5}
                      />
                    </td>
                    <td className="ev-col-ausente">
                      <input
                        type="checkbox"
                        checked={row.ausente}
                        onChange={() => toggleAusente(row.idAlumno)}
                        tabIndex={-1}
                        aria-label={`${row.nombre} ausente`}
                      />
                    </td>
                    <td>
                      <input
                        ref={(element) => { inputRefs.current[`${row.idAlumno}-comentario`] = element; }}
                        type="text"
                        className="ev-comentario-input"
                        value={row.comentario}
                        onChange={(event) => handleChange(row.idAlumno, 'comentario', event.target.value)}
                        onKeyDown={(event) => handleComentarioKeyDown(event, index, row.idAlumno)}
                        onBlur={() => commitRow(row.idAlumno)}
                        placeholder="Opcional"
                        maxLength={1000}
                        aria-label={`Comentario de ${row.nombre}`}
                      />
                    </td>
                    <td className="ev-col-status">
                      {row.status === 'saving' && <i className="fas fa-spinner fa-spin ev-st-saving" title="Guardando" aria-label="Guardando" />}
                      {row.status === 'saved' && <i className="fas fa-check-circle ev-st-saved" title="Guardado" aria-label="Guardado" />}
                      {row.status === 'dirty' && <i className="fas fa-circle ev-st-dirty" title="Sin guardar" aria-label="Sin guardar" />}
                      {row.status === 'invalid' && <i className="fas fa-exclamation-triangle ev-st-invalid" title={row.error} aria-label={row.error} />}
                      {row.status === 'error' && (
                        <button type="button" className="ev-st-error" onClick={() => commitRow(row.idAlumno, null, true)} title={`${row.error} Clic para reintentar.`}>
                          <i className="fas fa-exclamation-circle" aria-hidden="true" />
                          <i className="fas fa-redo" aria-hidden="true" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {rows.some((row) => row.status === 'invalid') && (
        <p className="ev-error-text">Hay notas con formato inválido: usá números de 1 a 10 (ej.: 7 o 7,5).</p>
      )}
    </div>
  );
});

EvaluacionNotasGrid.displayName = 'EvaluacionNotasGrid';

export default EvaluacionNotasGrid;
